import api from './api';
import axios from 'axios';

// tg — ссылка на Telegram.WebApp, тип описан в telegram-webapp.d.ts
export const tg = window.Telegram?.WebApp || null;

/**
 * Шаг 2: Проверка initData на сервере (HMAC).
 */
export async function verifyInitDataOnServer() {
    try {
        const resp = await axios.post('https://api.ezbazar.fvds.ru/api/telegram/verify', {
            initData: tg ? tg.initData : ""
        });
        return resp.data;
    } catch (err: any) {
        if (axios.isAxiosError(err)) {
            if (err.response) {
                console.error('ERROR: Сервер вернул ошибку', err.response.status);
                console.error('ERROR: Тело ответа:', err.response.data);
                console.error('ERROR: Заголовки:', err.response.headers);
            } else {
                console.error('ERROR: Нет ответа от сервера:', err.request);
            }
        } else {
            console.error('ERROR: Непредвиденная ошибка:', err);
        }
        return { result: false, errorText: 'Сетевая или 401-ошибка' };
    }
}

/**
 * Шаг 3: Применение темы (bg_color, text_color, ...)
 */
export function applyTelegramTheme(): void {
    if (!tg) {
        console.error("ERROR: tg отсутствует, невозможно применить тему");
        return;
    }
    const tp = tg.themeParams || {};
    const root = document.documentElement;

    // Устанавливаем фон
    if (tp.bg_color) {
        root.style.setProperty('--tg-over-bg-color', `#${tp.bg_color}`);
    } else {
        root.style.removeProperty('--tg-over-bg-color');
    }

    // Если тема тёмная, принудительно задаём текст белым,
    // иначе используем tp.text_color или оставляем значение по умолчанию
    if (tg.colorScheme === 'dark') {
        root.style.setProperty('--tg-over-text-color', '#ffffff');
    } else if (tp.text_color) {
        root.style.setProperty('--tg-over-text-color', `#${tp.text_color}`);
    } else {
        root.style.removeProperty('--tg-over-text-color');
    }

    // Устанавливаем цвет ссылок
    if (tp.link_color) {
        root.style.setProperty('--tg-over-link-color', `#${tp.link_color}`);
    } else {
        root.style.removeProperty('--tg-over-link-color');
    }
}

/**
 * Колбэк для themeChanged
 */
function themeChangedCallback() {
    applyTelegramTheme();
}

/**
 * Подписка на themeChanged
 */
export function subscribeOnThemeChange(): void {
    if (!tg) return;
    tg.onEvent('themeChanged', themeChangedCallback);
}

/**
 * Отписка от themeChanged
 */
export function unsubscribeOnThemeChange(): void {
    if (!tg) return;
    tg.offEvent('themeChanged', themeChangedCallback);
}

/**
 * Шаг 4: Управление интерфейсом (expand, closingConfirmation...)
 */
export function expandApp(): void {
    if (!tg) return;
    tg.expand();
}

export function enableClosingConfirmation(): void {
    if (!tg) return;
    (tg as any).enableClosingConfirmation?.();
}

export function disableClosingConfirmation(): void {
    if (!tg) return;
    (tg as any).disableClosingConfirmation?.();
}

export function enableSwipes(): void {
    if (!tg) return;
    (tg as any).enableVerticalSwipes?.();
}

export function disableSwipes(): void {
    if (!tg) return;
    (tg as any).disableVerticalSwipes?.();
}

/**
 * Шаг 5: Облачное хранилище CloudStorage (до 1024 записей)
 */
export function setCloudItem(key: string, value: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (!tg || !(tg as any).CloudStorage) {
            console.error("ERROR: CloudStorage недоступен при setCloudItem");
            reject(new Error('CloudStorage недоступен'));
            return;
        }
        (tg as any).CloudStorage.setItem(key, value, (err: any) => {
            if (err) {
                console.error("ERROR: setCloudItem вернул ошибку:", err);
                reject(err);
            } else {
                resolve();
            }
        });
    });
}

export function getCloudItem(key: string): Promise<string | null> {
    return new Promise((resolve, reject) => {
        if (!tg || !(tg as any).CloudStorage) {
            console.error("ERROR: CloudStorage недоступен при getCloudItem");
            reject(new Error('CloudStorage недоступен'));
            return;
        }
        (tg as any).CloudStorage.getItem(key, (err: any, data: string | null) => {
            if (err) {
                console.error("ERROR: getCloudItem вернул ошибку:", err);
                reject(err);
            } else {
                resolve(data);
            }
        });
    });
}

export function removeCloudItem(key: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (!tg || !(tg as any).CloudStorage) {
            console.error("ERROR: CloudStorage недоступен при removeCloudItem");
            reject(new Error('CloudStorage недоступен'));
            return;
        }
        (tg as any).CloudStorage.removeItem(key, (err: any) => {
            if (err) {
                console.error("ERROR: removeCloudItem вернул ошибку:", err);
                reject(err);
            } else {
                resolve();
            }
        });
    });
}

export function getCloudItems(keys: string[]): Promise<Record<string, string | null>> {
    return new Promise((resolve, reject) => {
        if (!tg || !(tg as any).CloudStorage) {
            console.error("ERROR: CloudStorage недоступен при getCloudItems");
            reject(new Error('CloudStorage недоступен'));
            return;
        }
        (tg as any).CloudStorage.getItems(keys, (err: any, data: Record<string, string | null>) => {
            if (err) {
                console.error("ERROR: getCloudItems вернул ошибку:", err);
                reject(err);
            } else {
                resolve(data);
            }
        });
    });
}

export function removeCloudItems(keys: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
        if (!tg || !(tg as any).CloudStorage) {
            console.error("ERROR: CloudStorage недоступен при removeCloudItems");
            reject(new Error('CloudStorage недоступен'));
            return;
        }
        (tg as any).CloudStorage.removeItems(keys, (err: any) => {
            if (err) {
                console.error("ERROR: removeCloudItems вернул ошибку:", err);
                reject(err);
            } else {
                resolve();
            }
        });
    });
}